import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { interval as observableInterval } from 'rxjs';

import { JsonHttpService } from '../http/json-http.service';
import { AuthenticationTokenService } from './authentication-token.service';
import { IAuthenticationService } from './authentication-service-interface';

/**
 * Because the JsonHttpService depends on the AuthenticationTokenService - we need to create this logic in a new service...
 */

@Injectable({
    providedIn: 'root'
})
export class AuthenticationTokenRefreshService implements IAuthenticationService {

    constructor(
        private http: JsonHttpService,
        private authenticationTokenService: AuthenticationTokenService
    ) {
    }

    refreshSubscription: any;
    clientId = 'EdiPortal_Web';

    /**
     * Login with username and password
     * @param username username to be used
     * @param password password to be used
     */
    public async login(username: string, password: string, authCode: string, rememberToken: boolean): Promise<void> {
        const options = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('Accept', 'application/json')
        };
        const data = await this.http.postWithoutAccessToken('api/login/get-refresh-token',
            { username, password, clientId: this.clientId, authCode }, options, false).toPromise();
        this.authenticationTokenService.setAccessToken(data.accessToken);
        this.authenticationTokenService.setRefreshToken(data.refreshToken, rememberToken);
        this.scheduleRefresh();
    }

    /**
     * Login with Azure AD Authorization Code
     * @param authCode token to be used
     */
    public async loginWithAuthCode(authCode: string, rememberToken: boolean, redirectUri: string): Promise<void> {
        const options = {
            headers: new HttpHeaders()
                .set('Content-Type', 'application/json')
                .set('Accept', 'application/json')
        };
        const data = await this.http.postWithoutAccessToken('api/login/get-refresh-token-with-azure-auth-code',
            { authCode, clientId: this.clientId, redirectUri }, options, false).toPromise();
        this.authenticationTokenService.setAccessToken(data.accessToken);
        this.authenticationTokenService.setRefreshToken(data.refreshToken, rememberToken);
        this.scheduleRefresh();
    }

    public isAuthenticated(): Promise<boolean> {
        return Promise.resolve(this.authenticationTokenService.hasValidAccessToken());
    }

    /**
     * Call this method on application-startup.
     */
    public startup(): Promise<void> {
        if (this.authenticationTokenService.hasRefreshToken()) {

            return this.getNewJwt().then((success) => {
                if (success) {
                    this.scheduleRefresh();
                    return Promise.resolve();
                }
                else {
                    // TODO: Should this be handled?
                    console.log('Can\'t get access token...');
                    return Promise.reject('Can\'t get access token...');
                }
            });
        }
        return Promise.resolve(); // Nothing to wait for - so just return promise
    }

    scheduleRefresh() {

        let interval: number;

        if (this.authenticationTokenService.hasValidAccessToken()) {
            const tokenData = this.authenticationTokenService.getAccessTokenData();
            const jwtIat = tokenData.iat;
            const jwtExp = tokenData.exp;
            const iat = new Date(0);
            const exp = new Date(0);

            interval = (exp.setUTCSeconds(jwtExp) - iat.setUTCSeconds(jwtIat));
            interval = interval * 0.8; // Make sure the token doesn't expire before we request a new one...
            console.log('Refresh interval found: ' + interval);
        }

        if (!interval) {
            interval = 60000; // Default every minute
        }

        this.refreshSubscription = observableInterval(interval).subscribe(() => {
            console.log('Getting new access token');
            this.getNewJwt();
        });
    }

    public stop() {
        // Unsubscribe from the refresh
        if (this.refreshSubscription) {
            this.refreshSubscription.unsubscribe();
        }
    }

    getNewJwt(): Promise<any> {
        const refreshToken = this.authenticationTokenService.getRefreshToken();

        if (!refreshToken) { // No need to call backend, if no refresh-token is available.
            return Promise.resolve(false);
        }

        const requestArgs = { headers: this.http.generateHeaders(true) }; // Do not send credential when getting a new access-token
        return this.http.postWithoutAccessToken('api/login/get-access-token', { refreshToken: refreshToken, clientId: this.clientId }, requestArgs).toPromise()
            .then((accessToken: string) => {
                if (accessToken) {
                    this.authenticationTokenService.setAccessToken(accessToken);
                }

                if (accessToken) {
                    return Promise.resolve(true);
                }
                else {
                    return Promise.resolve(false);
                }
            }).catch((ex) => {
                Promise.resolve(false);
            });
    }
}
